import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const textClass = scrolled
    ? 'text-foreground-900'
    : 'text-white drop-shadow-sm';

  const subtleClass = scrolled
    ? 'text-foreground-600 hover:text-foreground-950'
    : 'text-white/90 hover:text-white';

  const handleNavClick = (id: string) => {
    setMobileOpen(false);
    scrollToSection(id);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-gradient-to-b from-black/30 to-transparent'
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-4 md:px-6 h-16">
        <button
          onClick={() => scrollToSection('home')}
          className={`text-lg font-heading italic tracking-tight whitespace-nowrap cursor-pointer ${textClass}`}
        >
          Sugargoo Spreadsheet
        </button>

        <div className="hidden md:flex items-center gap-7">
          <button
            onClick={() => scrollToSection('home')}
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${subtleClass}`}
          >
            Home
          </button>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDropdownOpen(!dropdownOpen);
              }}
              className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${subtleClass}`}
            >
              Categories
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-3 max-h-96 overflow-y-auto">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-4 mb-2">All Categories</p>
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100/80 flex-shrink-0">
                      <i className={`${cat.icon} text-accent-600 text-xs`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => scrollToSection('guide')}
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${subtleClass}`}
          >
            Shopping Guide
          </button>

          <button
            onClick={() => scrollToSection('blog')}
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${subtleClass}`}
          >
            Blog
          </button>

          <button
            onClick={() => scrollToSection('safety')}
            className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${subtleClass}`}
          >
            Safety
          </button>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer shadow-sm"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line text-base"></i>
          </a>
        </div>

        <div className="flex md:hidden items-center gap-3">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1 px-4 py-1.5 bg-primary-500 text-background-50 rounded-full text-xs font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
          >
            Buy Now
          </a>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`w-8 h-8 flex items-center justify-center cursor-pointer ${textClass}`}
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-4 py-5 flex flex-col gap-1">
          <button
            onClick={() => handleNavClick('home')}
            className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <i className="ri-home-4-line text-foreground-600"></i>
            </span>
            Home
          </button>

          <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3 pt-3 pb-1">Categories</p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                <span className="w-5 h-5 flex items-center justify-center rounded bg-accent-100/80 flex-shrink-0">
                  <i className={`${cat.icon} text-accent-600 text-xs`}></i>
                </span>
                {cat.name}
              </a>
            ))}
          </div>

          <div className="border-t border-background-200/60 mt-3 pt-3 space-y-1">
            <button
              onClick={() => handleNavClick('guide')}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer w-full text-left"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-book-open-line text-foreground-600"></i>
              </span>
              Shopping Guide
            </button>
            <button
              onClick={() => handleNavClick('blog')}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer w-full text-left"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-article-line text-foreground-600"></i>
              </span>
              Blog
            </button>
            <button
              onClick={() => handleNavClick('safety')}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer w-full text-left"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-shield-star-line text-foreground-600"></i>
              </span>
              Safety
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}